// PWA support
import { registerSW } from 'virtual:pwa-register'
registerSW({ immediate: true })

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap"
import "bootstrap-icons/font/bootstrap-icons.css";
// jQuery + lightbox2 are loaded lazily on the product-detail page (the only
// place the image gallery is used) — see Pages/Product/ProductDetail.vue.
import CookieService from './Services/CookieService';
import SEOService from './Services/SEOService';

// Make Bootstrap available globally
import * as bootstrap from 'bootstrap';
window.bootstrap = bootstrap;

// Make services available globally
window.CookieService = CookieService;
window.SEOService = SEOService;

// Configure axios globally
import axios from 'axios';
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Get CSRF token from meta tag
const token = document.head.querySelector('meta[name="csrf-token"]');
if (token) {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
}

// Emit bootstrap loaded event after a short delay to ensure it's ready
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        const event = new CustomEvent('bootstrap:loaded');
        document.dispatchEvent(event);
    }, 100);
});

import '../css/app.css';
import '../css/admin-tokens.css';

import { createApp, h } from 'vue';
//import { createSSRApp, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import TopNavBar from './Components/TopNavBar.vue';
import VueLazyload from '@jambonn/vue-lazyload'

/* FontAwesome icon component + tree-shaken icon registration (see icons.js —
   only the ~14 icons actually used, not the full ~1.1 MB fas/far sets). */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import './icons';

// ApexCharts is NOT registered globally — it's ~500 KB and only the product-page
// price graph uses it. PriceGraph.vue imports it lazily (defineAsyncComponent),
// so it loads as an async chunk only on pages that actually render a chart.

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'PriceCompare';

import tooltip from "./Directives/tooltip.js";

/*
 * Broen mellem to Inertia-versioner.
 *
 * Serveren er inertia-laravel v3. Den laegger sidedata i et selvstaendigt
 * <script data-page="app" type="application/json">-tag. Klienten er
 * @inertiajs/vue3 2.0.8, og den laeser data af #app[data-page].
 *
 * Paa alle SSR-rendrede sider ser man det aldrig: SSR-processen leverer
 * initialPage, og klienten roerer aldrig attributten. Men fejler SSR paa ÉN
 * side, falder Laravel tilbage til klient-rendering — og saa staar klienten
 * med JSON.parse(undefined) og en HELT HVID side.
 *
 * Det skete i produktionen 15/9 paa alle build-sider. Den udloesende fejl er
 * rettet (se SEOHead), men uoverensstemmelsen var det der gjorde en
 * SSR-fejl til en hvid side i stedet for en langsom side. Den her bro fjerner
 * hele fejlklassen med fire linjer.
 *
 * Den rigtige rettelse er at loefte klienten til 3.x. Det er en opgave for
 * sig, og indtil da skal en SSR-fejl ikke kunne slukke en side.
 */
const rod = document.getElementById('app');
const nyttelast = document.querySelector('script[data-page="app"][type="application/json"]');
if (rod && !rod.dataset.page && nyttelast) {
    rod.dataset.page = nyttelast.textContent;
}

createInertiaApp({
    title: (title) => `${title}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')).catch(() => {
        // Stale chunk after deploy — force full page reload
        window.location.reload();
    }),
    setup({ el, App, props, plugin }) {
        return createApp({ render: () => h(App, props) })
            .component('font-awesome-icon', FontAwesomeIcon)
            .component('TopNavBar', TopNavBar)
            .use(plugin)
            .use(ZiggyVue, Ziggy)
            .use(VueLazyload, {
                preLoad: 1.3,
                attempt: 1
            })
            .directive('tooltip', tooltip) // Define the 'tooltip' directive
            .mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});

// SPA page_view tracking — gtag config has send_page_view:false, so we fire
// page_view manually. Inertia 2's `navigate` event does NOT fire on the initial
// (SSR) page load, so single-page sessions (most organic landings) were never
// tracked. We therefore fire once on initial load AND on every navigation, with
// de-dup so we never double-count the same URL. Under denied consent these go to
// GA4 as cookieless pings (Consent Mode v2).
let lastTrackedUrl = null;
function trackPageView() {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
        return;
    }
    const url = window.location.pathname + window.location.search;
    if (url === lastTrackedUrl) {
        return;
    }
    lastTrackedUrl = url;
    window.gtag('event', 'page_view', {
        page_path: url,
        page_location: window.location.href,
        page_title: document.title,
    });
}

// Fire the initial page_view for the landing page (gtag() is defined inline in
// the document head, so calls queue into dataLayer until gtag.js finishes loading).
trackPageView();

// Fire on every subsequent Inertia navigation.
router.on('navigate', () => trackPageView());
